import { X } from 'lucide-react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import {
  BOOLEAN_OPTIONS,
  FILTER_FIELDS,
  OPERATORS,
  getDefaultOperator,
  getFilterField,
} from '../filterFields';
import { FiltersFormValues } from '../filtersSchema';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { cn } from '@/lib/utils';

interface ColumnFilterRowProps {
  index: number;
  control: Control<FiltersFormValues>;
  setValue: UseFormSetValue<FiltersFormValues>;
  error?: string;
  onRemove: () => void;
}

export function ColumnFilterRow({
  index,
  control,
  setValue,
  error,
  onRemove,
}: ColumnFilterRowProps) {
  const prefix = `filters.${index}` as const;
  const field = useWatch({ control, name: `${prefix}.field` });
  const operator = useWatch({ control, name: `${prefix}.operator` });
  const selectedValues = useWatch({ control, name: `${prefix}.values` });

  const definition = getFilterField(field);
  const operators = OPERATORS[definition?.type ?? 'text'];
  const needsValue = operators.find(
    (item) => item.value === operator,
  )?.needsValue;

  function handleFieldChange(next: string) {
    setValue(`${prefix}.field`, next, { shouldValidate: true });
    setValue(`${prefix}.operator`, getDefaultOperator(next));
    setValue(`${prefix}.value`, '');
    setValue(`${prefix}.values`, []);
  }

  function toggleOption(option: string) {
    const next = selectedValues.includes(option)
      ? selectedValues.filter((item) => item !== option)
      : [...selectedValues, option];

    setValue(`${prefix}.values`, next, { shouldValidate: true });
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={field} onValueChange={handleFieldChange}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Column" />
          </SelectTrigger>
          <SelectContent>
            {FILTER_FIELDS.map((item) => (
              <SelectItem key={item.field} value={item.field}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Controller
          control={control}
          name={`${prefix}.operator`}
          render={({ field: operatorField }) => (
            <Select
              value={operatorField.value}
              onValueChange={operatorField.onChange}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {operators.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />

        {needsValue && definition?.type === 'enum' && (
          <div className="flex flex-wrap gap-1">
            {definition.options?.map((option) => {
              const active = selectedValues.includes(option);

              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggleOption(option)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    active
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100',
                  )}
                >
                  {option}
                </button>
              );
            })}
          </div>
        )}

        {needsValue && definition?.type === 'boolean' && (
          <Controller
            control={control}
            name={`${prefix}.value`}
            render={({ field: valueField }) => (
              <Select
                value={valueField.value}
                onValueChange={valueField.onChange}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Value" />
                </SelectTrigger>
                <SelectContent>
                  {BOOLEAN_OPTIONS.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        )}

        {needsValue &&
          (definition?.type === 'text' || definition?.type === 'number') && (
            <Controller
              control={control}
              name={`${prefix}.value`}
              render={({ field: valueField }) => (
                <Input
                  {...valueField}
                  type={definition.type === 'number' ? 'number' : 'text'}
                  placeholder="Value"
                  className="w-[220px]"
                />
              )}
            />
          )}

        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Remove filter"
          onClick={onRemove}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
